import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { NursePageHeader } from '../ui';
import NurseFileQueueList from '../panels/NurseFileQueueList';
import { useNurseWorkspace } from '../nurseWorkspaceContext';
import {
  NURSE_QUEUE_FILTERS,
  getQueueBucketLabel,
  matchesNurseQueueFilter,
  passageSectionForQueueBucket,
  classifyNursingQueueItem
} from '../utils/nurseQueueFilters';
import './NurseQueuePage.css';

const ACTIVE_QUEUE_FILTERS = NURSE_QUEUE_FILTERS.filter((f) => f.id !== 'done');

function chipLabel(filterId, Q) {
  if (filterId === 'all') return Q.FILTER_ALL;
  return getQueueBucketLabel(filterId);
}

function matchesPatientSearch(consultation, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    consultation.patient_name,
    consultation.member_matricule,
    consultation.member_company_name
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function emptyMessageForFilter(filterId, counts, Q) {
  if (filterId === 'done') return Q.EMPTY_DONE;
  if (filterId === 'pending') return Q.EMPTY_PENDING;
  if (filterId === 'care') return Q.EMPTY_CARE;
  if (filterId === 'exam') return Q.EMPTY_EXAM;
  if (filterId === 'all' && (counts.all ?? 0) === 0 && (counts.done ?? 0) > 0) {
    return Q.EMPTY_ACTIVE_ONLY;
  }
  return Q.EMPTY;
}

function emptyTitleForFilter(filterId, isDoneView, Q) {
  if (isDoneView) return Q.EMPTY_DONE_TITLE;
  if (filterId === 'pending') return Q.EMPTY_PENDING_TITLE;
  if (filterId === 'care') return Q.EMPTY_CARE_TITLE;
  if (filterId === 'exam') return Q.EMPTY_EXAM_TITLE;
  return Q.EMPTY_ACTIVE_TITLE;
}

function footerSummary(filterId, count, isDoneView, Q) {
  const plural = count > 1 ? 's' : '';
  if (isDoneView) return `${count} passage${plural} terminé${plural}`;
  if (filterId === 'all') return `${count} passage${plural} en cours`;
  return `${count} passage${plural} — ${chipLabel(filterId, Q).toLowerCase()}`;
}

export default function NurseQueuePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterFromUrl = searchParams.get('filter') || 'all';
  const [queueFilter, setQueueFilter] = useState(() =>
    NURSE_QUEUE_FILTERS.some((f) => f.id === filterFromUrl) ? filterFromUrl : 'all'
  );
  const [patientSearch, setPatientSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const {
    workSiteId,
    workSites,
    consultations,
    loading,
    error,
    loadQueue,
    counts,
    copy,
    consultationEditPath
  } = useNurseWorkspace();

  const Q = copy.QUEUE;

  const currentSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );

  const isDoneView = queueFilter === 'done';

  useEffect(() => {
    const f = searchParams.get('filter') || 'all';
    if (NURSE_QUEUE_FILTERS.some((x) => x.id === f)) setQueueFilter(f);
  }, [searchParams]);

  const setFilter = useCallback(
    (id) => {
      setQueueFilter(id);
      if (id === 'all') setSearchParams({});
      else setSearchParams({ filter: id });
    },
    [setSearchParams]
  );

  const setSegment = useCallback(
    (segment) => {
      if (segment === 'done') {
        setFilter('done');
        return;
      }
      if (queueFilter === 'done') {
        setFilter('all');
      }
    },
    [setFilter, queueFilter]
  );

  const filtered = useMemo(() => {
    const rows = consultations.filter(
      (c) => matchesNurseQueueFilter(c, queueFilter) && matchesPatientSearch(c, patientSearch)
    );
    return [...rows].sort((a, b) => {
      const da = new Date(a.consultation_date).getTime();
      const db = new Date(b.consultation_date).getTime();
      return isDoneView ? db - da : da - db;
    });
  }, [consultations, queueFilter, patientSearch, isDoneView]);

  const openConsultation = useCallback(
    (consultation) => {
      const bucket = classifyNursingQueueItem(consultation);
      navigate(
        consultationEditPath(consultation.id, {
          section: passageSectionForQueueBucket(bucket)
        })
      );
    },
    [navigate, consultationEditPath]
  );

  const pageDescription = currentSite
    ? `${Q.PAGE_SUBTITLE} · ${currentSite.name}`
    : Q.PAGE_SUBTITLE;

  const handleRefresh = useCallback(async () => {
    if (!workSiteId || refreshing) return;
    setRefreshing(true);
    try {
      await loadQueue({ silent: true });
    } finally {
      setRefreshing(false);
    }
  }, [workSiteId, refreshing, loadQueue]);

  const refreshBusy = loading || refreshing;

  return (
    <div className="nr-file recv-dash-pro--main">
      <NursePageHeader badge={Q.BADGE} title={Q.PAGE_TITLE} description={pageDescription} />

      {!workSiteId ? (
        <Alert variant="warning">{Q.NO_SITE}</Alert>
      ) : (
        <Card className="border-0 recv-dash-card recv-dash-card--elevated recv-queue-card nr-file-card">
          <Card.Header className="recv-dash-card__head recv-queue-card__head border-0 bg-transparent nr-file-card__head">
            <div className="nr-file-card__toolbar">
              <div className="nr-file-segments" role="tablist" aria-label={Q.SEGMENTS_ARIA}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isDoneView}
                  className={`nr-file-segments__btn${!isDoneView ? ' is-active' : ''}`}
                  onClick={() => setSegment('active')}
                >
                  {Q.SEGMENT_ACTIVE}
                  <span className="nr-file-segments__count">{counts.all ?? 0}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isDoneView}
                  className={`nr-file-segments__btn${isDoneView ? ' is-active' : ''}`}
                  onClick={() => setSegment('done')}
                >
                  {Q.SEGMENT_DONE}
                  <span className="nr-file-segments__count">{counts.done ?? 0}</span>
                </button>
              </div>

              <div className="nr-file-card__actions">
                <div className="nr-file-card__search">
                  <Icon name="search" size={16} className="nr-file-card__search-icon" aria-hidden />
                  <Form.Control
                    type="search"
                    className="nr-file-card__search-input"
                    placeholder={Q.SEARCH_PLACEHOLDER}
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    aria-label={Q.SEARCH_ARIA}
                  />
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="recv-queue-card__refresh"
                  onClick={handleRefresh}
                  disabled={!workSiteId || refreshBusy}
                  aria-label={Q.REFRESH_ARIA}
                >
                  {refreshBusy ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Icon name="refresh" size={14} aria-hidden />
                      {Q.REFRESH}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {!isDoneView ? (
              <div className="nr-file-filters" role="group" aria-label={Q.FILTERS_ARIA}>
                {ACTIVE_QUEUE_FILTERS.map((filter) => {
                  const isActive = queueFilter === filter.id;
                  const countKey = filter.id === 'all' ? 'all' : filter.id;
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      className={`nr-file-filters__chip${isActive ? ' is-active' : ''}`}
                      aria-pressed={isActive}
                      onClick={() => setFilter(filter.id)}
                    >
                      {chipLabel(filter.id, Q)}
                      <span className="nr-file-filters__count">{counts[countKey] ?? 0}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </Card.Header>

          <Card.Body className="p-0">
            {error ? (
              <Alert variant="danger" className="m-3 mb-0">
                {error}
              </Alert>
            ) : null}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="success" size="sm" />
                <p className="text-muted small mt-2 mb-0">{Q.LOADING}</p>
              </div>
            ) : (
              <NurseFileQueueList
                rows={filtered}
                doneView={isDoneView}
                onOpen={openConsultation}
                emptyTitle={emptyTitleForFilter(queueFilter, isDoneView, Q)}
                emptyDescription={emptyMessageForFilter(queueFilter, counts, Q)}
              />
            )}
          </Card.Body>

          {!loading && filtered.length > 0 ? (
            <Card.Footer className="nr-file-card__foot bg-transparent border-0">
              {footerSummary(queueFilter, filtered.length, isDoneView, Q)}
              {currentSite ? ` · ${currentSite.name}` : ''}
            </Card.Footer>
          ) : null}
        </Card>
      )}
    </div>
  );
}
